import { useState, useRef, useEffect } from "react";
import { MessageSquare, Calendar, AlertTriangle, Tag } from "lucide-react";
import { priorityCfg } from "./data";
import CategoryEditPopup from "./CategoryEditPopup";
import PersonCell from "./PersonCell";

function fmtDate(v) {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d)) return String(v);
  return String(d.getDate()).padStart(2,"0")+"-"+String(d.getMonth()+1).padStart(2,"0")+"-"+d.getFullYear();
}
function fmtDateNaive(v) {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d)) return String(v);
  return String(d.getUTCDate()).padStart(2,"0")+"-"+String(d.getUTCMonth()+1).padStart(2,"0")+"-"+d.getUTCFullYear();
}
function nameList(v) {
  if (!v) return null;
  if (Array.isArray(v)) return v.join(", ") || null;
  return String(v);
}


const STATUS_CFG = {
  "open":             { bg:"#EEEDFE", color:"#534AB7", label:"Open"           },
  "in progress":      { bg:"#D1FAE5", color:"#065F46", label:"In Progress"    },
  "in-progress":      { bg:"#D1FAE5", color:"#065F46", label:"In Progress"    },
  "on hold":          { bg:"#FCE7F3", color:"#9D174D", label:"On Hold"        },
  "on-hold":          { bg:"#FCE7F3", color:"#9D174D", label:"On Hold"        },
  "completed":        { bg:"#D1FAE5", color:"#065F46", label:"Complete"       },
  "complete":         { bg:"#D1FAE5", color:"#065F46", label:"Complete"       },
  "approval pending": { bg:"#FEF3C7", color:"#92400E", label:"Approval Await" },
  "closed":           { bg:"#F3F4F6", color:"#374151", label:"Closed"         },
};

function MorePopup({ task, onClose }) {
  const ref = useRef();
  useEffect(() => {
    const fn = e => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, [onClose]);

  const accountable = task.task_created_by_name || task.task_created_by;
  const responsible = nameList(task.assigned_to_name);
  const consulted   = nameList(task.observer_to_name);
  const informed    = nameList(task.informer_to_name);
  const cats        = Array.isArray(task.functional_categories) ? task.functional_categories : [];
  const isOverdue   = task.completion_sla && new Date(task.completion_sla) < new Date()
                      && !["completed","complete","closed"].includes((task.task_status||"").toLowerCase());

  const Row = ({ label, val, danger }) => (
    <div className="flex gap-2 py-[5px] border-b-[0.5px] border-[#F1F5F9] items-start">
      <span className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-[.6px] w-[90px] shrink-0 pt-0.5">{label}</span>
      {val
        ? <span className={`text-[12px] flex items-center gap-1 ${danger ? "text-[#DC2626] font-semibold" : "text-[#374151]"}`}>
            {danger && <AlertTriangle size={10} color="#DC2626"/>}{val}
          </span>
        : <span className="text-[11px] text-[#CBD5E1]">—</span>}
    </div>
  );

  return (
    <div ref={ref} onClick={e => e.stopPropagation()}
      className="absolute top-[calc(100%+6px)] left-0 right-0 bg-white border border-[#E2E8F0] rounded-xl shadow-[0_8px_24px_rgba(0,0,0,.12)] z-[100] px-[14px] py-[10px]">
      <div className="flex justify-between items-center mb-2">
        <span className="text-[11px] font-bold text-[#64748B] uppercase tracking-[.5px]">All details</span>
        <button onClick={onClose} className="bg-transparent border-none cursor-pointer text-[#94A3B8] text-[16px] leading-none p-0">×</button>
      </div>
      <Row label="Accountable"  val={accountable}/>
      <Row label="Responsible"  val={responsible}/>
      <Row label="Consulted"    val={consulted}/>
      <Row label="Informed"     val={informed}/>
      <Row label="Created At"   val={fmtDate(task.task_created_at)}/>
      <Row label="Start Date"   val={fmtDateNaive(task.start_date)}/>
      <Row label="Deadline"     val={fmtDateNaive(task.completion_sla)} danger={isOverdue}/>
      <Row label="Completed"    val={fmtDate(task.completed_at)}/>
      <Row label="Completed by" val={task.completed_by}/>
      {cats.length > 0 && (
        <div className="flex gap-2 py-[5px] items-start">
          <span className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-[.6px] w-[90px] shrink-0 pt-0.5">Categories</span>
          <div className="flex flex-wrap gap-1">
            {cats.map((c,i) => (
              <span key={i} className="text-[10px] px-[7px] py-[2px] rounded-[6px] bg-[#EFF6FF] text-[#1d4ed8] border border-[#BFDBFE]">{c}</span>
            ))}
          </div>
        </div>
      )}
      {task.current_stage && <Row label="Stage" val={task.current_stage}/>}
    </div>
  );
}

function ExtraCatsPill({ cats = [], onCatClick }) {
  const [open, setOpen] = useState(false);
  const ref   = useRef(null);
  const timer = useRef(null);
  const cancel    = () => clearTimeout(timer.current);
  const closeLater = () => { timer.current = setTimeout(() => setOpen(false), 150); };

  return (
    <div className="relative inline-flex" ref={ref}>
      <span
        onMouseEnter={() => { cancel(); setOpen(true); }}
        onMouseLeave={closeLater}
        onClick={e => { e.stopPropagation(); onCatClick && onCatClick(); }}
        className="text-[10px] font-bold px-[6px] py-[2px] rounded-full bg-[#EFF6FF] text-[#1d4ed8] border border-[#BFDBFE] cursor-pointer select-none leading-none flex items-center"
      >
        +{cats.length}
      </span>
      {open && (
        <div
          onMouseEnter={cancel}
          onMouseLeave={closeLater}
          className="absolute bottom-[calc(100%+6px)] left-0 z-[200] bg-white border border-slate-200 rounded-xl shadow-[0_8px_24px_rgba(99,102,241,.15)] overflow-hidden min-w-[140px]"
        >
          <div className="px-3 py-1.5 border-b border-slate-100 text-[10px] font-bold uppercase tracking-wide text-slate-400">
            {cats.length} more
          </div>
          <div className="p-2 flex flex-col gap-1">
            {cats.map((c, i) => (
              <span key={i} className="text-[10px] px-2 py-[3px] rounded-[6px] bg-[#EFF6FF] text-[#1d4ed8] border border-[#BFDBFE] font-medium">{c}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ExtraMembersPill({ names = [], ids = [] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const timer = useRef(null);

  const cancel   = () => clearTimeout(timer.current);
  const closeLater = () => { timer.current = setTimeout(() => setOpen(false), 150); };

  return (
    <div className="relative inline-flex" ref={ref}>
      <span
        onMouseEnter={() => { cancel(); setOpen(true); }}
        onMouseLeave={closeLater}
        className="text-[10px] font-bold px-[6px] py-[2px] rounded-full bg-indigo-50 text-indigo-600 border border-indigo-200 cursor-default select-none leading-none flex items-center"
      >
        +{names.length}
      </span>
      {open && (
        <div
          onMouseEnter={cancel}
          onMouseLeave={closeLater}
          className="absolute bottom-[calc(100%+6px)] left-0 z-[200] bg-white border border-slate-200 rounded-xl shadow-[0_8px_24px_rgba(99,102,241,.15)] overflow-hidden min-w-[160px]"
        >
          <div className="px-3 py-1.5 border-b border-slate-100 text-[10px] font-bold uppercase tracking-wide text-slate-400">
            {names.length} more
          </div>
          <div className="p-2 flex flex-col gap-1">
            {names.map((n, i) => (
              <PersonCell key={i} name={n} personId={ids[i]} idx={i + 1} size={24} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function TaskCard({ task, onTaskClick, onCommentClick, onCatsUpdated }) {
  const [showMore,  setShowMore]  = useState(false);
  const [catPopup,  setCatPopup]  = useState(false);
  const [localCats, setLocalCats] = useState(
    Array.isArray(task.functional_categories) ? task.functional_categories : []
  );

  useEffect(() => {
    setLocalCats(Array.isArray(task.functional_categories) ? task.functional_categories : []);
  }, [task.functional_categories]);

  const pc        = priorityCfg[(task.priority||"").toUpperCase()] || priorityCfg["MEDIUM"];
  const sc        = STATUS_CFG[(task.task_status||"").toLowerCase()] || STATUS_CFG["open"];
  const isOverdue = task.completion_sla && new Date(task.completion_sla) < new Date()
                    && !["completed","complete","closed"].includes((task.task_status||"").toLowerCase());
  const commentCount = task.notes_count || (Array.isArray(task.notes) ? task.notes.length : 0);

  const accountable   = task.task_created_by_name || task.task_created_by || null;
  const accountableId = task.task_created_by_id   || null;
  const respNames     = Array.isArray(task.assigned_to_name) ? task.assigned_to_name
                        : task.assigned_to_name ? [task.assigned_to_name] : [];
  const respIds       = Array.isArray(task.assigned_to_ids) ? task.assigned_to_ids : [];

  return (
    <div className="bg-white rounded-xl border border-[#E8EAF0] shadow-[0_1px_3px_rgba(0,0,0,.05)] relative transition-[box-shadow,border-color] duration-150 hover:shadow-[0_4px_16px_rgba(0,0,0,.09)] hover:border-[#C7D2FE]">

      {showMore && <MorePopup task={{...task, functional_categories:localCats}} onClose={()=>setShowMore(false)}/>}
      {catPopup && (
        <CategoryEditPopup
          taskId={task._id}
          currentCategories={localCats}
          onClose={() => setCatPopup(false)}
          onSaved={newCats => {
            setLocalCats(newCats);
            if (onCatsUpdated) onCatsUpdated(task._id, newCats);
          }}
        />
      )}

      <div className="px-[14px] pt-3 pb-[10px]">
        <div className="flex items-center justify-between mb-2">
          <span className="font-mono text-[10px] bg-[#EEF2FF] text-[#4338CA] px-[7px] py-[2px] rounded-[5px] font-bold">#{task.unique_seq_id||"—"}</span>
          <div className="flex items-center gap-[5px]">
            <span onClick={e => { e.stopPropagation(); setCatPopup(true); }}
              title={localCats.length > 0 ? localCats.join(", ") : "Add categories"}
              className="flex items-center gap-[3px] text-[10px] text-[#1d4ed8] bg-[#EFF6FF] hover:bg-[#DBEAFE] border border-[#BFDBFE] px-[7px] py-[2px] rounded-[6px] cursor-pointer font-semibold">
              <Tag size={10}/>{localCats.length > 0 ? localCats.length : "+"}
            </span>
            <span onClick={e => { e.stopPropagation(); onCommentClick && onCommentClick(task); }}
              title="Comments"
              className="flex items-center gap-[3px] text-[10px] text-[#2563EB] bg-[#EFF6FF] hover:bg-[#DBEAFE] border border-[#BFDBFE] px-[7px] py-[2px] rounded-[6px] cursor-pointer font-semibold">
              <MessageSquare size={10}/>{commentCount > 0 ? commentCount : ""}
            </span>
            <span
              className="text-[10px] font-bold px-2 py-[2px] rounded-[20px] border"
              style={{ background:pc.bg, color:pc.color, borderColor:pc.border }}
            >
              {(task.priority||"").toUpperCase()||"—"}
            </span>
          </div>
        </div>

        <div onClick={() => onTaskClick && onTaskClick(task)}
          className="text-[13px] font-semibold text-[#1d4ed8] leading-[1.4] mb-[9px] cursor-pointer line-clamp-2 hover:underline">
          {task.task_type||"—"}
        </div>

        <div className="flex flex-wrap gap-[5px]">
          <span className="text-[10px] font-semibold px-2 py-[2px] rounded-[20px]" style={{ background:sc.bg, color:sc.color }}>{sc.label}</span>
          {task.product_name && (
            <span className="text-[10px] px-2 py-[2px] rounded-full bg-[#F1F5F9] text-[#475569] font-medium">{task.product_name}</span>
          )}
          {task.current_stage && (
            <span className="text-[10px] px-2 py-[2px] rounded-full bg-[#F0FDF4] text-[#15803D] border border-[#BBF7D0] font-medium">{task.current_stage}</span>
          )}
          {localCats.length > 0 && (
            <span onClick={e => { e.stopPropagation(); setCatPopup(true); }}
              className="text-[10px] px-2 py-[2px] rounded-[6px] cursor-pointer bg-[#EFF6FF] hover:bg-[#DBEAFE] text-[#1d4ed8] border border-[#BFDBFE] font-medium">
              {localCats[0]}
            </span>
          )}
          {localCats.length > 1 && (
            <ExtraCatsPill cats={localCats.slice(1)} onCatClick={() => setCatPopup(true)} />
          )}
        </div>
      </div>

      <div className="flex items-center justify-between px-[14px] py-[7px] border-t-[0.5px] border-[#F1F5F9] bg-[#FAFBFE]">
        <div className="flex items-center gap-[6px]">
          {/* Responsible — PersonCell gives hover RACI popup same as list view */}
          {respNames.length > 0 ? (
            <div className="flex items-center gap-[5px]">
              <PersonCell name={respNames[0]} personId={respIds[0]} idx={0} size={30} />
              {respNames.length > 1 && (
                <ExtraMembersPill names={respNames.slice(1)} ids={respIds.slice(1)} />
              )}
            </div>
          ) : accountable ? (
            <PersonCell name={accountable} personId={accountableId} idx={0} size={30} />
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          {task.completion_sla && (
            <span className={`flex items-center gap-[3px] text-[11px] font-semibold ${isOverdue ? "text-[#DC2626]" : "text-[#2563EB]"}`}>
              {isOverdue && <AlertTriangle size={10} color="#DC2626"/>}
              <Calendar size={10}/>{fmtDateNaive(task.completion_sla)}
            </span>
          )}
          <button onClick={e => { e.stopPropagation(); setShowMore(p=>!p); }}
            className={`flex items-center gap-[3px] text-[11px] font-semibold rounded-[7px] px-[9px] py-[3px] cursor-pointer transition-all duration-150 border-[0.5px] ${
              showMore
                ? "text-[#7C3AED] bg-[#EEEDFE] border-[#AFA9EC]"
                : "text-[#64748B] bg-[#F1F5F9] border-[#E2E8F0]"
            }`}>
            More
            <svg
              width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
              className={`transition-transform duration-200 ${showMore ? "rotate-180" : "rotate-0"}`}
            >
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CardView({ tasks = [], loading = false, onTaskClick, onCommentClick }) {
  if (loading) return (
    <div className="flex items-center justify-center py-[60px] gap-2.5">
      <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke="#E2E8F0" strokeWidth="4"/>
        <path d="M4 12a8 8 0 018-8" stroke="#7C3AED" strokeWidth="4" strokeLinecap="round"/>
      </svg>
      <span className="text-[13px] text-[#94A3B8]">Loading tasks…</span>
      <style>{`@keyframes cvSpin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if (!tasks.length) return (
    <div className="text-center py-[60px] text-[#CBD5E1] text-[13px]">No tasks found</div>
  );

  return (
    <div className="px-4 pt-3 pb-6">
      <div className="grid gap-[14px] [grid-template-columns:repeat(auto-fill,minmax(300px,1fr))]">
        {tasks.map((task, i) => (
          <TaskCard key={task._id||i} task={task} onTaskClick={onTaskClick} onCommentClick={onCommentClick}/>
        ))}
      </div>
    </div>
  );
}